/** @jsxRuntime classic */
/** @jsx jsx */

import { ReactNode } from 'react';
import { css, jsx } from '@emotion/react';
import styled from '@emotion/styled';
import paths from 'constants/paths';
import { useTrackEvent } from 'tracking/useTrackEvent';
import { mediaQueries } from 'components/mediaQueries';
import { Link } from 'components/Link/Link';
import { CommonLinkText } from 'components/typography';
import { MiniArticleCard as MiniArticleCardType } from 'data/global/types';
import { Colors, Typography } from '@zapier/design-system';
import {
  ArticleImage,
  defaultImageQueryParams,
} from 'components/Images/ArticleImage/ArticleImage';
import { ImageQueryParams } from 'components/Images/RichImageComponent/RichImageComponent';

type Props = { article: MiniArticleCardType };

const miniCardImageParams: Readonly<ImageQueryParams> = {
  ...defaultImageQueryParams,
  w: '896',
};

export const MiniArticleCard = ({ article }: Props) => {
  const trackEvent = useTrackEvent();

  const ArticleLink = ({ children }: { children: ReactNode }) => {
    return (
      <Link
        href={paths.blogArticle(article.slug)}
        data-testid="miniArticleCard-articleLink"
        onClick={(event) =>
          trackEvent('linkClicked', {
            linkText: article.title,
            href: paths.blogArticle(article.slug),
            path: window.location.pathname,
            posYFromTop: event.clientY,
          })
        }
      >
        {children}
      </Link>
    );
  };

  const truncateTitle = (title: string): string => {
    if (title.length < 45) {
      return title;
    }
    const truncatedTitle = title.slice(0, 45);
    const lastEmptySpace = truncatedTitle.lastIndexOf(' ');

    return truncatedTitle.slice(0, lastEmptySpace) + '...';
  };

  return (
    <CardWrapper data-testid="miniArticleCard">
      <ArticleLink>
        <Image image={article.image} queryParams={miniCardImageParams} />
      </ArticleLink>
      <TextWrapper>
        <Link
          href={article.categoryAssemblyHref}
          onClick={(event) =>
            trackEvent('linkClicked', {
              linkText: article.categoryAssemblyTitle,
              href: article.categoryAssemblyHref,
              path: window.location.pathname,
              posYFromTop: event.clientY,
            })
          }
        >
          <CommonLinkText
            css={css`
              ${mediaQueries.tablet} {
                margin-bottom: 10px;
              }
            `}
          >
            {article.categoryAssemblyTitle}
          </CommonLinkText>
        </Link>
        <ArticleLink>
          <Title>{article.title}</Title>
          <ShortTitle>{truncateTitle(article.title)}</ShortTitle>
        </ArticleLink>
      </TextWrapper>
    </CardWrapper>
  );
};

const Title = styled.p`
  color: ${Colors.neutral800};
  display: none;
  ${Typography.smallPrint1Semibold}
  ${mediaQueries.tablet} {
    display: grid;
    ${Typography.paragraph1Semibold}
  }
  ${mediaQueries.desktop} {
    ${Typography.paragraph3Bold}
  }
`;

const ShortTitle = styled(Title)`
  display: block;
  ${mediaQueries.tablet} {
    display: none;
  }
`;

const CardWrapper = styled.div`
  display: grid;
  grid-column-gap: 10px;
  grid-row-gap: 10px;
  grid-template-columns: unset;
  ${mediaQueries.tablet} {
    grid-template-columns: min-content 1fr;
    grid-column-gap: 40px;
    padding: 0;
    &:hover {
      & ${Title} {
        color: ${Colors.blueJeans};
      }
      img {
        opacity: 50%;
      }
    }
  }
  ${mediaQueries.desktop} {
    grid-template-columns: 1fr;
  }
`;

const Image = styled(ArticleImage)`
  width: 100%;
  height: 100%;
  ${mediaQueries.tablet} {
    height: 130px;
    width: 230px;
  }
  ${mediaQueries.desktop} {
    margin-bottom: 15px;
    height: 132px;
    width: 100%;
  }
`;

const TextWrapper = styled.div`
  align-self: center;
`;
